import { Injectable } from 'angular-ts-decorators';

import { interval, Observable, SchedulerLike } from 'rxjs';
import { map, distinctUntilChanged, skip } from 'rxjs/operators';

/**
 * Service that provides a way to get events accross different apps
 * (subdomains). As cookies the only thing that is really shared between
 * different subdomaings, it's pretty much the only way to send informations
 * across.
 *
 * Service that encapsulates nasty workaround the limitations thats comes from:
 * * having the application split across multiple domains
 * * https://github.com/patrickkettner/cookie-change-events not being in the
 * browsers yet
 *
 * When any of above will change, we will be able to achive the same thing
 * in much easier way.
 *
 * With this service, we query the cookies periodically, waiting on some
 * change. We do this, as we don't have any other way to listen for cookies
 * changes.
 */
@Injectable('cookieStreams')
export class CookieStreams {
  private readonly checkInterval: number = 2000;
  private readonly check$: Observable<number>;

  constructor(
    private ipCookie,
    scheduler: SchedulerLike,
  ) {
    'ngInject';

    // consider igoring safary & moving over to events
    // https://developer.mozilla.org/en-US/docs/Mozilla/Add-ons/WebExtensions/API/cookies/onChanged
    this.check$ = interval(this.checkInterval, scheduler);
  }

  /**
   * @param {String} key for which change we are waiting
   * @returns {Stream}
   * stream that emit everytime when the value of the cookies is changed
   * Stream; emits when the values changes
   */
  getChange$(key: string): Observable<any> {
    return this.check$.pipe(
      map(() => this.ipCookie(key)),
      distinctUntilChanged(),
      skip(1),
    );
  }
  /**
   * @param {String} key for which change we are waiting
   * @returns {Stream}
   * stream that emit every value, as soon as it's changes
   * Stream; emits the cookie value; with first value included
   */
  getValue$(key: string): Observable<any> {
    return this.check$.pipe(
      map(() => this.ipCookie(key)),
      distinctUntilChanged(),
    );
  }
}
